/**
 * RevoGridReport
 *
 * Report view using RevoGrid for a battle-tested, feature-rich data grid.
 * Provides Excel-like functionality with:
 * - Column sorting, filtering, resizing
 * - Row grouping (with custom aggregation display)
 * - Virtual scrolling for large datasets
 * - Column reordering
 *
 * @see https://rv-grid.com/guide/framework.react.overview
 */

import { useMemo, useCallback, useEffect, useRef } from "react";
import { RevoGrid } from "@revolist/revogrid-react";
import type {
  ColumnRegular,
  DataType,
} from "@revolist/revogrid";
import { defineCustomElements } from "@revolist/revogrid/loader";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import type { ReportData, AggregationType } from "@/lib/erp/reports";

// Initialize RevoGrid custom elements (required for web component)
defineCustomElements();

interface RevoGridReportProps {
  doctype: string;
  data: ReportData | undefined;
  visibleColumns?: string[];
  aggregations?: Record<string, AggregationType>;
  groupByField?: string;
  onRowClick?: (row: Record<string, unknown>) => void;
  isLoading?: boolean;
  className?: string;
}

export function RevoGridReport({
  // doctype is kept in props interface for API consistency but not used internally
  data,
  visibleColumns = [],
  aggregations = {},
  groupByField,
  onRowClick,
  isLoading,
  className,
}: RevoGridReportProps) {
  const gridRef = useRef<HTMLRevoGridElement | null>(null);

  // Convert our data to RevoGrid format
  const source: DataType[] = useMemo(() => {
    if (!data) return [];

    if (data.mode === "flat") {
      return data.data as DataType[];
    }

    // For grouped mode, flatten the groups into rows with group headers
    // RevoGrid's free version supports basic grouping via grouping.props
    // We'll let RevoGrid handle the grouping display
    if (data.mode === "grouped") {
      // Return flat data - we need to fetch it separately or use a different approach
      // For now, return the groups as summary rows
      return data.groups.map((group) => ({
        __isGroupRow: true,
        __groupValue: group.group_value,
        __groupCount: group.count,
        [data.group_by_field]: group.group_value ?? "(No Value)",
        count: group.count,
        ...Object.fromEntries(
          Object.entries(aggregations).map(([field, aggType]) => [
            `${field}_${aggType}`,
            group[`${field}_${aggType}`],
          ])
        ),
      })) as DataType[];
    }

    return [];
  }, [data, aggregations]);

  // Build columns configuration
  const columns: ColumnRegular[] = useMemo(() => {
    if (!data) return [];

    // Determine which columns to show
    let columnsToShow: string[] = [];

    if (visibleColumns.length > 0) {
      columnsToShow = visibleColumns;
    } else if (data.mode === "flat" && data.data.length > 0) {
      // Get columns from first row
      columnsToShow = Object.keys(data.data[0]);
    } else if (data.mode === "grouped") {
      // For grouped, show group field + count + aggregations
      columnsToShow = [data.group_by_field, "count"];
      Object.entries(aggregations).forEach(([field, aggType]) => {
        columnsToShow.push(`${field}_${aggType}`);
      });
    }

    return columnsToShow.map((fieldname) => {
      const isAggColumn = fieldname.includes("_sum") ||
                          fieldname.includes("_avg") ||
                          fieldname.includes("_count");
      const isCountColumn = fieldname === "count";

      return {
        prop: fieldname,
        name: formatColumnHeader(fieldname),
        sortable: true,
        filter: true,
        size: isAggColumn || isCountColumn ? 120 : 150,
        cellProperties: ({ value }) => {
          // Right-align numeric columns
          if (typeof value === "number" || isAggColumn || isCountColumn) {
            return {
              style: {
                textAlign: "right",
                fontVariantNumeric: "tabular-nums",
              },
            };
          }
          return {};
        },
      } as ColumnRegular;
    });
  }, [data, visibleColumns, aggregations]);

  // Handle row click
  const handleRowClick = useCallback(
    (event: CustomEvent) => {
      if (!onRowClick) return;
      const { detail } = event;
      if (detail?.data) {
        onRowClick(detail.data as Record<string, unknown>);
      }
    },
    [onRowClick]
  );

  // Attach event listener for row clicks
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;

    const handler = (e: Event) => handleRowClick(e as CustomEvent);
    grid.addEventListener("rowclick", handler);

    return () => {
      grid.removeEventListener("rowclick", handler);
    };
  }, [handleRowClick]);

  // Grouping configuration
  const grouping = useMemo(() => {
    if (!groupByField || data?.mode !== "flat") return undefined;
    return {
      props: [groupByField],
      expandedAll: true,
    };
  }, [groupByField, data?.mode]);

  if (isLoading) {
    return (
      <div className={cn("rounded-sm border bg-card", className)}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading report...</span>
        </div>
      </div>
    );
  }

  if (!data || source.length === 0) {
    return (
      <div className={cn("rounded-sm border bg-card", className)}>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">No data available</div>
        </div>
      </div>
    );
  }

  // For grouped data from backend (aggregated), show summary table
  if (data.mode === "grouped") {
    return (
      <div className={cn("rounded-sm border bg-card overflow-hidden", className)}>
        <RevoGrid
          ref={gridRef}
          columns={columns}
          source={source}
          theme="compact"
          resize={true}
          readonly={true}
          range={false}
          style={{ height: "500px" }}
        />
        {/* Totals footer */}
        <div className="border-t bg-muted/30 px-4 py-3 flex items-center justify-between text-sm">
          <span className="font-semibold">Total</span>
          <div className="flex items-center gap-6">
            <span className="tabular-nums">
              {data.totals.count?.toLocaleString() ?? 0} items
            </span>
            {Object.entries(aggregations).map(([field, aggType]) => {
              const key = `${field}_${aggType}`;
              const value = data.totals[key];
              return (
                <span key={key} className="tabular-nums">
                  {formatColumnHeader(key)}: {formatValue(value, aggType)}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // For flat data, use full RevoGrid with optional client-side grouping
  return (
    <div className={cn("rounded-sm border bg-card overflow-hidden", className)}>
      <RevoGrid
        ref={gridRef}
        columns={columns}
        source={source}
        grouping={grouping}
        theme="compact"
        resize={true}
        readonly={true}
        range={false}
        filter={true}
        style={{ height: "500px" }}
      />
      {/* Footer with total count */}
      <div className="border-t bg-muted/30 py-2 px-4 text-sm text-muted-foreground">
        Total: {data.total.toLocaleString()} rows
      </div>
    </div>
  );
}

// Helper to format column headers
function formatColumnHeader(fieldname: string): string {
  // Handle aggregation columns
  if (fieldname.endsWith("_sum")) {
    return `SUM(${fieldname.replace("_sum", "")})`;
  }
  if (fieldname.endsWith("_avg")) {
    return `AVG(${fieldname.replace("_avg", "")})`;
  }
  if (fieldname.endsWith("_count")) {
    return `COUNT(${fieldname.replace("_count", "")})`;
  }

  // Convert snake_case to Title Case
  return fieldname
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// Helper to format values
function formatValue(value: unknown, aggType?: AggregationType): string {
  if (value === null || value === undefined) return "-";
  if (typeof value === "number") {
    if (aggType === "count") {
      return value.toLocaleString();
    }
    return value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  return String(value);
}
